/**
 * Tuna News HQ — theme scripts
 *
 * Everything here is progressive enhancement. With JS disabled the nav links
 * are still reachable, both platform columns still render stacked, and the
 * page is fully readable — only the decorative and convenience layers are lost.
 */

// Imported so Vite emits the compiled stylesheet as assets/theme.css.
// WordPress enqueues that file directly; it is never injected by this script.
import '../scss/main.scss';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* -------------------------------------------------------------------------
 * Mobile navigation
 * ---------------------------------------------------------------------- */
function initNav() {
  const toggle = document.querySelector('.tnhq-menu-toggle');
  const nav = document.getElementById('tnhq-nav');
  if (!toggle || !nav) return;

  const close = () => {
    nav.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
  };

  toggle.addEventListener('click', () => {
    const open = toggle.getAttribute('aria-expanded') === 'true';
    nav.classList.toggle('is-open', !open);
    toggle.setAttribute('aria-expanded', String(!open));
  });

  // Escape closes the panel and returns focus to the control that opened it.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && nav.classList.contains('is-open')) {
      close();
      toggle.focus();
    }
  });

  // Clicking a link navigates; make sure the panel isn't left open behind it.
  nav.addEventListener('click', (e) => {
    if (e.target.closest('a')) close();
  });

  // Reset state when crossing back to the desktop layout, otherwise the
  // panel's inline open class leaks into the horizontal nav.
  const desktop = window.matchMedia('(min-width: 900px)');
  desktop.addEventListener('change', (e) => { if (e.matches) close(); });
}

/* -------------------------------------------------------------------------
 * Sticky header shadow
 * ---------------------------------------------------------------------- */
function initStickyHeader() {
  const header = document.querySelector('.tnhq-header');
  if (!header) return;

  // A sentinel + IntersectionObserver avoids a scroll listener firing on
  // every frame.
  const sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:100%;pointer-events:none;';
  document.body.prepend(sentinel);

  new IntersectionObserver(
    ([entry]) => header.classList.toggle('is-stuck', !entry.isIntersecting),
    { threshold: 0 }
  ).observe(sentinel);
}

/* -------------------------------------------------------------------------
 * Rumble / Facebook mobile tab switcher
 *
 * Built entirely in JS so the no-JS experience stays as two stacked, fully
 * visible sections. The tablist is only meaningful below the desktop
 * breakpoint; above it both columns show and the tabs are hidden by CSS.
 * ---------------------------------------------------------------------- */
function initPlatformTabs() {
  document.querySelectorAll('[data-tnhq-split]').forEach((split) => {
    const cols = Array.from(split.querySelectorAll('[data-platform]'));
    if (cols.length < 2) return;

    const tablist = document.createElement('div');
    tablist.className = 'tnhq-tabs';
    tablist.setAttribute('role', 'tablist');
    tablist.setAttribute('aria-label', 'Choose a video platform');

    const tabs = cols.map((col, i) => {
      const platform = col.dataset.platform;
      const label = col.dataset.label || platform;

      // Ensure stable ids to wire aria-controls/labelledby both ways.
      if (!col.id) col.id = `tnhq-panel-${platform}-${i}`;

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'tnhq-tabs__btn';
      tab.id = `tnhq-tab-${platform}-${i}`;
      tab.textContent = label;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', col.id);
      col.setAttribute('role', 'tabpanel');
      col.setAttribute('aria-labelledby', tab.id);

      tablist.append(tab);
      return tab;
    });

    const select = (index, focus = false) => {
      tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        // Roving tabindex: only the active tab is in the tab order.
        tab.tabIndex = active ? 0 : -1;
        cols[i].hidden = active ? false : isMobile();
      });
      if (focus) tabs[index].focus();
    };

    const isMobile = () => !window.matchMedia('(min-width: 960px)').matches;

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(i));
      tab.addEventListener('keydown', (e) => {
        const last = tabs.length - 1;
        let next = null;
        if (e.key === 'ArrowRight') next = i === last ? 0 : i + 1;
        if (e.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
        if (e.key === 'Home') next = 0;
        if (e.key === 'End') next = last;
        if (next !== null) {
          e.preventDefault();
          select(next, true);
        }
      });
    });

    split.prepend(tablist);
    split.classList.add('is-tabbed');

    let current = 0;
    select(current);

    // Crossing the breakpoint: reveal every column on desktop, restore the
    // single-panel view on mobile.
    window.matchMedia('(min-width: 960px)').addEventListener('change', () => {
      if (isMobile()) {
        select(current);
      } else {
        cols.forEach((col) => { col.hidden = false; });
      }
    });

    tabs.forEach((tab, i) => tab.addEventListener('click', () => { current = i; }));
  });
}

/* -------------------------------------------------------------------------
 * Click-to-load video players
 *
 * The poster and play control are server-rendered; the platform iframe is
 * only created when the visitor asks for it. This keeps a page of videos from
 * booting several third-party players — and their cookies — on load.
 * ---------------------------------------------------------------------- */
function initEmbeds() {
  document.querySelectorAll('[data-tnhq-embed]').forEach((facade) => {
    const button = facade.querySelector('.tnhq-embed__play');
    if (!button) return;

    button.addEventListener('click', () => {
      const src = facade.dataset.tnhqEmbed;
      if (!src) return;

      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.title = facade.dataset.tnhqEmbedTitle || 'Video player';
      iframe.loading = 'lazy';
      iframe.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0;';

      // Replace the facade contents outright so the poster image is not left
      // sitting behind a transparent player.
      facade.replaceChildren(iframe);
      facade.classList.remove('tnhq-embed--facade');
      iframe.focus();
    });
  });
}

/* -------------------------------------------------------------------------
 * Decorative bubbles
 *
 * Generated rather than authored so the markup stays clean. Skipped entirely
 * for reduced-motion users and narrow screens.
 * ---------------------------------------------------------------------- */
function initBubbles() {
  if (reducedMotion || window.innerWidth < 640) return;

  const host = document.querySelector('.tnhq-bubbles');
  if (!host) return;

  const COUNT = 14;
  const frag = document.createDocumentFragment();

  for (let i = 0; i < COUNT; i += 1) {
    const size = 6 + Math.random() * 22;
    const bubble = document.createElement('span');
    bubble.className = 'tnhq-bubble';
    bubble.style.cssText = [
      `left:${Math.random() * 100}%`,
      `width:${size}px`,
      `height:${size}px`,
      `animation-duration:${12 + Math.random() * 16}s`,
      `animation-delay:${-Math.random() * 20}s`,
    ].join(';');
    frag.append(bubble);
  }

  host.append(frag);
}

/* -------------------------------------------------------------------------
 * Lazy-reveal on scroll
 * ---------------------------------------------------------------------- */
function initReveal() {
  const targets = document.querySelectorAll('[data-tnhq-reveal]');
  if (!targets.length || reducedMotion) {
    targets.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  targets.forEach((el) => io.observe(el));
}

/* ---------------------------------------------------------------------- */
function init() {
  initNav();
  initStickyHeader();
  initPlatformTabs();
  initEmbeds();
  initBubbles();
  initReveal();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
